import React, { useMemo, useState } from "react";
import { FlaskConical, Syringe, Droplets, RotateCcw } from "lucide-react";

// Client-side reconstitution & dosing math for research protocols.
export const DosageCalculator = ({ recon, productName }) => {
  const [vialMg, setVialMg] = useState(recon.vial_mg);
  const [bacMl, setBacMl] = useState(recon.bac_ml);
  const [doseMcg, setDoseMcg] = useState(recon.dose_mcg);

  const reset = () => {
    setVialMg(recon.vial_mg);
    setBacMl(recon.bac_ml);
    setDoseMcg(recon.dose_mcg);
  };

  const r = useMemo(() => {
    const v = Number(vialMg) || 0;
    const b = Number(bacMl) || 0;
    const d = Number(doseMcg) || 0;
    if (v <= 0 || b <= 0) return null;
    const mgPerMl = v / b;
    const mcgPerMl = mgPerMl * 1000;
    const drawMl = d > 0 ? d / mcgPerMl : 0;
    const units = drawMl * 100; // U-100 insulin syringe
    const dosesPerVial = d > 0 ? (v * 1000) / d : 0;
    return { mgPerMl, mcgPerMl, drawMl, units, dosesPerVial };
  }, [vialMg, bacMl, doseMcg]);

  const Field = ({ label, value, onChange, suffix, step = 1, testid }) => (
    <label className="block">
      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </span>
      <div className="flex items-center border border-[#27272A] bg-ink-900 mt-1.5 focus-within:border-accent transition-colors">
        <input
          type="number"
          min="0"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 py-2.5 font-display font-bold text-lg text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid={testid}
        />
        <span className="px-3 font-mono-tech text-[11px] uppercase tracking-wider text-zinc-500 border-l border-[#27272A]">
          {suffix}
        </span>
      </div>
    </label>
  );

  const Stat = ({ icon: Icon, label, value, unit, testid }) => (
    <div className="bg-ink-900 border border-[#27272A] p-4">
      <div className="flex items-center gap-2 text-accent mb-2">
        <Icon size={15} />
        <span className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500">
          {label}
        </span>
      </div>
      <p className="font-display font-black text-2xl leading-none" data-testid={testid}>
        {value}
        <span className="font-mono-tech text-xs text-zinc-500 ml-1 font-normal">{unit}</span>
      </p>
    </div>
  );

  return (
    <div className="border border-[#27272A] bg-ink-800 clip-corner" data-testid="dosage-calculator">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#27272A]">
        <div className="flex items-center gap-2.5">
          <FlaskConical size={18} className="text-accent" />
          <h3 className="font-display font-extrabold text-lg tracking-tight">
            RECONSTITUTION &amp; DOSAGE CALCULATOR
          </h3>
        </div>
        <button
          onClick={reset}
          className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 hover:text-accent transition-colors"
          data-testid="dosage-reset"
        >
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="p-6 grid md:grid-cols-3 gap-4">
        <Field label="Peptide in vial" value={vialMg} onChange={setVialMg} suffix="mg" testid="calc-vial-mg" />
        <Field label="BAC water added" value={bacMl} onChange={setBacMl} suffix="ml" step="0.5" testid="calc-bac-ml" />
        <Field label="Desired dose" value={doseMcg} onChange={setDoseMcg} suffix="mcg" step="50" testid="calc-dose-mcg" />
      </div>

      {r && (
        <div className="px-6 pb-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Droplets} label="Concentration" value={r.mcgPerMl.toLocaleString(undefined, { maximumFractionDigits: 0 })} unit="mcg/ml" testid="calc-concentration" />
          <Stat icon={Syringe} label="Draw volume" value={r.drawMl.toFixed(3)} unit="ml" testid="calc-draw-ml" />
          <Stat icon={Syringe} label="On insulin pin" value={Math.round(r.units)} unit="units" testid="calc-units" />
          <Stat icon={FlaskConical} label="Doses / vial" value={Math.floor(r.dosesPerVial)} unit="doses" testid="calc-doses" />
        </div>
      )}

      <div className="px-6 pb-6">
        <div className="bg-ink-900 border border-[#27272A] p-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-accent block mb-1.5">
              Reconstitution guide
            </span>
            Slowly inject the {bacMl}ml of bacteriostatic water down the inside wall of the {productName} vial
            (do not aim directly at the powder). Gently swirl — never shake — until fully dissolved.
            {recon.note ? ` ${recon.note}` : ""} Draw <strong className="text-white">{r ? Math.round(r.units) : 0} units</strong> on
            a U-100 insulin syringe for a <strong className="text-white">{doseMcg}mcg</strong> research dose.
          </p>
          <p className="font-mono-tech text-[10px] text-zinc-600 mt-3 leading-relaxed">
            Figures are for laboratory reference only. Not dosing advice. For research use only — not for
            human consumption.
          </p>
        </div>
      </div>
    </div>
  );
};
